import {CoinsSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/page/basket_sections.types';
import {useTranslation} from '@aboutyou/i18n';
import {Fade} from '@aboutyou/transitions';
import {
  Alert,
  COLORS,
  ContentPopperProps,
  FONT_SIZES,
  FONT_WEIGHTS,
  GenericIconName,
  Icon,
  SkeletonBlock,
  Text,
  Toggle,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useState} from 'react';
import {CoinsStickyRow} from './CoinsStickyRow';
import {RedeemCoinsPopup} from './ReedemCoinsPopup';
import {RedeemCoinsStep, useRedeemCoins} from './hooks/useRedeemCoins';

interface BasketCoinsViewProps extends CoinsSection {
  LayoutComponent: ContentPopperProps['LayoutComponent'];
  className?: string;
  stickyHeightRef?: React.MutableRefObject<{
    checkoutButtonHeight: number;
    stickyRowsHeight: number;
  }>;
}

export const BasketCoinsView: React.FC<BasketCoinsViewProps> = ({
  badge,
  className,
  LayoutComponent,
  sticky,
  stickyHeightRef,
  subtitle,
  title,
  type,
}) => {
  const [toggled, setToggled] = useState(
    type?.$case === 'available' && type.available.applied,
  );
  const {t} = useTranslation();

  const {redeemCoins, redeemCoinsPopupData, step, isRedeemCoinsPopupShown} =
    useRedeemCoins();

  const onToggle = async () => {
    const newState = !toggled;

    setToggled(newState);
    await redeemCoins({
      toggleTransition: newState,
      onError: () => {
        setToggled(!newState);
      },
    });
  };

  return (
    <>
      {!toggled && sticky && stickyHeightRef && (
        <CoinsStickyRow sticky={sticky} stickyHeightRef={stickyHeightRef} />
      )}
      <div className={className} data-testid="basketCoins">
        <Container>
          <BasketCoinsRow>
            <Icon asset={GenericIconName.CoinsDoubleColor} size="S" />
            <TitleContainer>
              {step === RedeemCoinsStep.Loading ? (
                <LoadingTitleSkeletons />
              ) : (
                <Title
                  title={title}
                  subtitle={subtitle}
                  badge={!toggled ? badge : undefined}
                  showSuccessIcon={
                    toggled &&
                    step === RedeemCoinsStep.Applied &&
                    !isRedeemCoinsPopupShown
                  }
                />
              )}
            </TitleContainer>
            <Toggle
              checked={toggled}
              onChange={onToggle}
              disabled={
                type?.$case === 'error' || type?.$case === 'unavailable'
              }
              aria-label={toggled ? t('Remove coins') : t('Redeem coins')}
            />
          </BasketCoinsRow>

          {type?.$case === 'unavailable' && (
            <Alert body={type.unavailable.notification} type="info" />
          )}
          {type?.$case === 'error' && (
            <Alert body={type.error.notification} type="error" />
          )}
        </Container>
      </div>
      {type?.$case === 'available' && (
        <RedeemCoinsPopup
          isOpen={isRedeemCoinsPopupShown}
          step={step}
          data={{
            coins: type.available.coins,
            equivalentAmount: type.available.equivalentAmount,
            enabledHint: redeemCoinsPopupData,
          }}
          LayoutComponent={LayoutComponent}
        />
      )}
    </>
  );
};

const LoadingTitleSkeletons = () => {
  return (
    <>
      <StyledSkeletonBlock
        data-testid="coinsSkeletons"
        $width="100%"
        $height="1.25rem"
      />
      <StyledSkeletonBlock $width="70%" $height="1.25rem" />
    </>
  );
};

const Title: React.FC<{
  title: string;
  subtitle: string;
  badge?: string;
  showSuccessIcon?: boolean;
}> = ({title, subtitle, badge, showSuccessIcon}) => {
  return (
    <>
      <TitleRow>
        <TitleText size="px14" weight="mediumHeavy">
          {title}
        </TitleText>
        {badge && <Badge>{badge}</Badge>}
        <Fade duration={200} mounted={!!showSuccessIcon}>
          <Icon
            asset={GenericIconName.AlertSuccessFill}
            size="XS"
            fill={COLORS['Alerts.Success.Primary']}
          />
        </Fade>
      </TitleRow>
      <Text size="px12" weight="mediumBold">
        {subtitle}
      </Text>
    </>
  );
};

const StyledSkeletonBlock = styled(SkeletonBlock)`
  margin-bottom: 0.125rem;
`;

const TitleRow = styled.div`
  display: flex;
  gap: 0.25rem;
  align-items: center;
  width: 100%;
`;

const TitleText = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TitleContainer = styled.div`
  flex: 1;
  min-width: 0;
`;

export const Badge = styled.div`
  background-color: ${COLORS['Alerts.Success.Primary']};
  padding: 3px 5px;
  border-radius: 14px;
  color: ${COLORS.White};
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  height: 17px;
  line-height: 1.1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BasketCoinsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const Container = styled.div`
  padding: 1rem;
  background-color: ${COLORS.White};
  display: flex;
  gap: 0.5rem;
  flex-direction: column;
`;
